/**
 * PublicLeadFormPage — oeffentliches Anfrage-Formular pro Firma (/anfrage/:companyId).
 * Erfasst gclid/fbclid aus der URL und meldet Conversions (Consent-Gate).
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicLeadFormPage() {
  const { companyId } = useParams();
  const [params] = useSearchParams();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", website: "" });
  const formTs = useRef(Date.now());
  const [suggests, setSuggests] = useState([]);

  const onCompanyChange = (v) => {
    setForm((f) => ({ ...f, company: v }));
    if (v.trim().length >= 3) {
      clearTimeout(window.__zefixT);
      window.__zefixT = setTimeout(() => {
        axios.get(`${API}/zefix/public-suggest`, { params: { q: v.trim() } })
          .then((r) => setSuggests(r.data.results || []))
          .catch(() => setSuggests([]));
      }, 350);
    } else setSuggests([]);
  };
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/lead-form/${companyId}`)
      .then((r) => setCompany(r.data))
      .catch(() => setNotFound(true));
  }, [companyId]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true); setError("");
    try {
      await axios.post(`${API}/public/lead-form/${companyId}`, {
        ...form,
        consent,
        form_ts: formTs.current,
        gclid: params.get("gclid") || undefined,
        gbraid: params.get("gbraid") || undefined,
        wbraid: params.get("wbraid") || undefined,
        fbclid: params.get("fbclid") || undefined,
        page_url: params.get("page_url") || window.location.href,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Senden fehlgeschlagen — bitte erneut versuchen.");
    } finally {
      setSending(false);
    }
  };

  const brand = company?.brand_color || "#2563EB";
  const isEmbed = params.get("embed") === "1";

  if (notFound) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB] text-slate-500" data-testid="lead-form-not-found">Formular nicht gefunden.</div>;
  }
  if (!company) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB]"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className={isEmbed ? "bg-transparent p-1" : "min-h-screen bg-[#F6F8FB] flex items-center justify-center p-4"} data-testid="public-lead-form-page">
      <div className={isEmbed ? "w-full" : "w-full max-w-md"}>
        {!isEmbed && (
        <div className="flex items-center gap-3 mb-6 justify-center">
          {company.logo_url ? (
            <img src={company.logo_url} alt="" className="w-11 h-11 rounded-xl object-cover" />
          ) : (
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold" style={{ backgroundColor: brand }}>
              {company.name.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="leading-tight">
            <h1 className="text-xl font-bold text-slate-900">{company.name}</h1>
            {company.tagline && <p className="text-xs text-slate-500">{company.tagline}</p>}
          </div>
        </div>
        )}
        <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-6">
          {done ? (
            <div className="text-center py-8" data-testid="lead-form-success">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: brand }} />
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Vielen Dank!</h2>
              <p className="text-sm text-slate-500">Deine Anfrage ist bei uns eingegangen — wir melden uns so schnell wie möglich.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-900">Anfrage senden</h2>
              <Input data-testid="lead-name-input" required placeholder="Name *" value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <Input data-testid="lead-email-input" type="email" placeholder="E-Mail" value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              <Input data-testid="lead-phone-input" placeholder="Telefon" value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              <div className="relative">
                <Input data-testid="lead-company-input" placeholder="Firma (optional)" value={form.company}
                  onChange={(e) => onCompanyChange(e.target.value)} autoComplete="off" />
                {suggests.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-44 overflow-y-auto" data-testid="lead-company-suggests">
                    {suggests.map((s) => (
                      <button key={s.uid} type="button"
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
                        onClick={() => { setForm((f) => ({ ...f, company: s.name })); setSuggests([]); }}>
                        {s.name} <span className="text-[10px] text-slate-400">{s.seat}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Textarea data-testid="lead-message-input" placeholder="Deine Nachricht…" className="min-h-[90px]" value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
              {/* Honeypot */}
              <input type="text" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <label className="flex items-start gap-2 text-xs text-slate-500 cursor-pointer">
                <Checkbox data-testid="lead-consent-checkbox" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
                <span>Ich bin einverstanden, dass meine Angaben zur Bearbeitung der Anfrage und zur Erfolgsmessung von Werbekampagnen verwendet werden (Marketing-Consent).</span>
              </label>
              {error && <p className="text-xs text-red-500" data-testid="lead-form-error">{error}</p>}
              <Button
                data-testid="lead-submit-btn"
                type="submit"
                className="w-full gap-2 text-white"
                style={{ backgroundColor: brand }}
                disabled={sending || !form.name.trim() || (!form.email.trim() && !form.phone.trim())}
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Anfrage senden
              </Button>
              <p className="text-[10px] text-slate-400 text-center">E-Mail oder Telefon wird benötigt, damit wir dich erreichen können.</p>
            </form>
          )}
        </div>
        {!isEmbed && <p className="text-center text-slate-400 text-[10px] mt-4">Powered by InvictaFlow Marketing</p>}
      </div>
    </div>
  );
}
